import { Show } from 'solid-js'
import { TOKENS_RELOAD_WITH_SUBSCRIPTION } from '/common/token-plans'

const ModelPageSignupModal = (props: {
  show: boolean
  avatarUrl?: string
  creatorName?: string
  characterName?: string
  subscribeToModel: () => void
  showTokensModal: () => void
  setShowSubscribeModal: (show: boolean) => void
  paymentUrl?: string
  subscribing?: boolean
}) => {
  return (
    <Show when={props.show}>
      <style>{subscribeModalCSS}</style>
      <div class="sub-overlay" onClick={() => props.setShowSubscribeModal(false)}>
        <div
          class="sub-modal"
          classList={{ 'payment-mode': !!props.paymentUrl }}
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-label="VIP Pass offer"
        >
          <div class="sub-card" classList={{ 'payment-mode': !!props.paymentUrl }}>
            {/* Close button */}
            <button
              class="sub-close"
              onClick={() => props.setShowSubscribeModal(false)}
              aria-label="Close"
            >
              &times;
            </button>

            <Show
              when={!props.paymentUrl}
              fallback={
                <div class="flex h-[880px] max-h-[calc(100vh-140px)] w-full flex-col p-3 md:p-6">
                  <iframe
                    src={props.paymentUrl}
                    class="h-full w-full rounded-lg"
                    style="border: 0;"
                    allow="payment"
                  />
                </div>
              }
            >
              {/* Photo region (top half) */}
              <div class="sub-image">
                <img
                  src={props.avatarUrl}
                  alt={
                    props.creatorName && props.characterName
                      ? `${props.creatorName} as ${props.characterName}`
                      : 'Character'
                  }
                />
                <span class="sub-eyebrow">Exclusive Access</span>
                <div class="sub-creator">
                  <div class="sub-creator-eyebrow">
                    VIP Pass &middot; {props.characterName || 'Character'}
                  </div>
                  <div class="sub-creator-name">
                    {props.creatorName ? `${props.creatorName}'s` : "Your AI's"}
                    <br />
                    <em>Private Page</em>
                  </div>
                </div>
              </div>

              {/* Perforated divider with price stamp */}
              <div class="sub-perf">
                <div class="sub-perf-line" />
                <div class="sub-perf-stamp">
                  <span class="price">$7.99</span>
                  <span class="per">/ mo</span>
                </div>
              </div>

              {/* Content section (bottom half) */}
              <div class="sub-content">
                <h2 class="sub-title">
                  Unlock the <em>full experience</em>
                </h2>

                <ul class="sub-benefits">
                  <li>
                    <span class="sub-check">&#10003;</span> Unlimited live chat
                  </li>
                  <li>
                    <span class="sub-check">&#10003;</span> {TOKENS_RELOAD_WITH_SUBSCRIPTION} token
                    welcome bonus
                  </li>
                  <li>
                    <span class="sub-check">&#10003;</span> Free monthly tokens
                  </li>
                  <li>
                    <span class="sub-check">&#10003;</span> Custom image generation
                  </li>
                  <li>
                    <span class="sub-check">&#10003;</span> Full exclusive gallery
                  </li>
                  <li>
                    <span class="sub-check">&#10003;</span> Cancel anytime
                  </li>
                </ul>

                <button
                  class="sub-cta"
                  onClick={props.subscribeToModel}
                  disabled={props.subscribing}
                >
                  {props.subscribing ? 'Activating...' : 'Activate VIP Pass'}
                </button>

                <div class="sub-skip">
                  <button onClick={props.showTokensModal}>
                    No thanks &mdash; just need tokens
                  </button>
                </div>

                <div class="sub-trust">
                  <span>&#128274; Secure</span>
                  <span>&middot;</span>
                  <span>Cancel anytime</span>
                  <span>&middot;</span>
                  <span>18+</span>
                </div>
              </div>
            </Show>
          </div>
        </div>
      </div>
    </Show>
  )
}

/* ── All CSS for the subscribe modal ── */
const subscribeModalCSS = `
/* Overlay */
.sub-overlay {
  position: fixed;
  inset: 0;
  z-index: 200;
  background: rgba(4, 3, 10, 0.82);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  animation: subFadeIn 0.25s ease;
}

@keyframes subFadeIn {
  from { opacity: 0; }
  to   { opacity: 1; }
}

/* Outer gradient border ring */
.sub-modal {
  position: relative;
  width: 100%;
  max-width: 440px;
  max-height: calc(100dvh - 40px);
  padding: 2px;
  border-radius: 26px;
  background:
    conic-gradient(from var(--sub-border-angle, 0deg) at 50% 50%,
      #8A2BE2 0%, #FF3CAC 20%, #00E5FF 40%, #FF3CAC 60%, #8A2BE2 80%, #8A2BE2 100%);
  box-shadow:
    0 30px 80px rgba(138,43,226,0.35),
    0 0 0 1px rgba(255,255,255,0.04),
    0 0 80px rgba(255,60,172,0.18);
  animation:
    subSlideIn 0.4s cubic-bezier(.2,.9,.3,1.15),
    subBorderSpin 6s linear infinite;
  display: flex;
  flex-direction: column;
}

@keyframes subSlideIn {
  from { opacity: 0; transform: translateY(24px) scale(0.94); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
}

@keyframes subBorderSpin {
  to { --sub-border-angle: 360deg; }
}

/* Fallback: animate via filter hue-rotate for browsers lacking @property */
@supports not (animation-name: subBorderSpin) {
  .sub-modal {
    animation:
      subSlideIn 0.4s cubic-bezier(.2,.9,.3,1.15),
      subBorderHue 6s linear infinite;
  }
  @keyframes subBorderHue {
    to { filter: hue-rotate(360deg); }
  }
}

/* Payment mode: suppress VIP modal effects so the Epoch page loads clean.
   Static gradient border all the way around (no spin), no glow. */
.sub-modal.payment-mode {
  background: linear-gradient(135deg, #8A2BE2 0%, #FF3CAC 50%, #00E5FF 100%);
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.45), 0 0 0 1px rgba(255, 255, 255, 0.04);
  animation: subSlideIn 0.4s cubic-bezier(.2,.9,.3,1.15);
}
@supports not (animation-name: subBorderSpin) {
  .sub-modal.payment-mode {
    animation: subSlideIn 0.4s cubic-bezier(.2,.9,.3,1.15);
    filter: none;
  }
}

/* Inner card */
.sub-card {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: linear-gradient(170deg, #0e0b1f 0%, #07060d 100%);
  border-radius: 24px;
  overflow: hidden;
  position: relative;
}

/* Holographic shimmer */
.sub-card::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(115deg,
    transparent 30%,
    rgba(0,229,255,0.08) 45%,
    rgba(255,60,172,0.10) 50%,
    rgba(255,255,255,0.05) 55%,
    transparent 70%);
  background-size: 250% 250%;
  animation: subShine 5.5s ease-in-out infinite;
  mix-blend-mode: screen;
  pointer-events: none;
  z-index: 3;
  border-radius: 24px;
}

@keyframes subShine {
  0%        { background-position: -60% -60%; }
  55%, 100% { background-position: 160% 160%; }
}

/* Payment mode: remove the holographic shine sweep over the iframe */
.sub-card.payment-mode::after {
  content: none;
  display: none;
  animation: none;
}

/* Close button */
.sub-close {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid rgba(255,255,255,0.18);
  background: rgba(0,0,0,0.5);
  backdrop-filter: blur(6px);
  color: rgba(255,255,255,0.85);
  font-size: 20px;
  line-height: 1;
  display: grid;
  place-items: center;
  cursor: pointer;
  z-index: 10;
  transition: background 0.2s, color 0.2s, transform 0.2s;
}
.sub-close:hover {
  background: rgba(255,60,172,0.55);
  color: #fff;
  transform: rotate(90deg);
}

/* ── Photo region (top half) ── */
.sub-image {
  position: relative;
  height: 340px;
  flex-shrink: 0;
  overflow: hidden;
}
.sub-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center 18%;
  display: block;
}

/* Brand-tinted overlays */
.sub-image::before {
  content: '';
  position: absolute;
  inset: 0;
  background:
    radial-gradient(ellipse at 10% 10%, rgba(138,43,226,0.45) 0%, transparent 55%),
    radial-gradient(ellipse at 100% 0%, rgba(0,229,255,0.18) 0%, transparent 50%);
  mix-blend-mode: overlay;
  pointer-events: none;
  z-index: 1;
}
.sub-image::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg,
    rgba(7,6,13,0.35) 0%,
    rgba(7,6,13,0) 30%,
    rgba(7,6,13,0) 55%,
    rgba(7,6,13,0.85) 100%);
  pointer-events: none;
  z-index: 1;
}

/* "EXCLUSIVE ACCESS" eyebrow pill */
.sub-eyebrow {
  position: absolute;
  top: 14px;
  left: 14px;
  z-index: 5;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 26px;
  padding: 0 12px 0 10px;
  border-radius: 999px;
  background: linear-gradient(135deg, rgba(255,60,172,0.85), rgba(138,43,226,0.85));
  border: 1px solid rgba(255,255,255,0.2);
  font-family: 'Rajdhani', sans-serif;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 1.6px;
  text-transform: uppercase;
  color: #fff;
  box-shadow: 0 4px 14px rgba(255,60,172,0.4);
}
.sub-eyebrow::before {
  content: '';
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 0 6px #fff;
  animation: subDotPulse 1.6s ease-in-out infinite;
}

@keyframes subDotPulse {
  0%, 100% { opacity: 1; }
  50%      { opacity: 0.3; }
}

/* Creator title block over photo bottom */
.sub-creator {
  position: absolute;
  left: 22px;
  right: 22px;
  bottom: 22px;
  z-index: 5;
}
.sub-creator-eyebrow {
  font-family: 'Rajdhani', sans-serif;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1.8px;
  text-transform: uppercase;
  color: #00E5FF;
  text-shadow: 0 0 12px rgba(0,229,255,0.55);
  margin-bottom: 4px;
}
.sub-creator-name {
  font-family: 'Bebas Neue', sans-serif;
  font-size: 36px;
  line-height: 0.95;
  letter-spacing: 1px;
  color: #fff;
  text-shadow: 0 2px 16px rgba(0,0,0,0.65);
}
.sub-creator-name em {
  font-style: normal;
  background: linear-gradient(90deg, #FF3CAC 0%, #00E5FF 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

/* ── Perforated divider (ticket stub feel) ── */
.sub-perf {
  position: relative;
  height: 28px;
  flex-shrink: 0;
  z-index: 4;
  background: transparent;
}
/* Side notches */
.sub-perf::before,
.sub-perf::after {
  content: '';
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #0a0814;
  box-shadow: 0 0 0 1px rgba(255,255,255,0.05) inset;
}
.sub-perf::before { left: -12px; }
.sub-perf::after  { right: -12px; }

/* Dashed perforation line */
.sub-perf-line {
  position: absolute;
  top: 50%;
  left: 14px;
  right: 14px;
  height: 1px;
  border-top: 1.5px dashed rgba(255,255,255,0.18);
  z-index: 1;
}

/* Price stamp */
.sub-perf-stamp {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 28px;
  padding: 0 16px;
  border-radius: 999px;
  background: #0a0814;
  border: 1px solid rgba(0,229,255,0.5);
  z-index: 2;
  font-family: 'Bebas Neue', sans-serif;
  letter-spacing: 1px;
}
.sub-perf-stamp .price {
  font-size: 18px;
  color: #00E5FF;
  text-shadow: 0 0 10px rgba(0,229,255,0.6);
  line-height: 1;
}
.sub-perf-stamp .per {
  font-family: 'Rajdhani', sans-serif;
  font-weight: 700;
  font-size: 10px;
  letter-spacing: 1.4px;
  text-transform: uppercase;
  color: rgba(255,255,255,0.55);
}

/* ── Content section (bottom half) ── */
.sub-content {
  position: relative;
  z-index: 2;
  padding: 22px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  overflow-y: auto;
  flex: 1;
}

.sub-title {
  font-family: 'Bebas Neue', sans-serif;
  font-size: 24px;
  line-height: 1.05;
  letter-spacing: 0.8px;
  text-align: center;
  color: #fff;
  margin: 0;
}
.sub-title em {
  font-style: normal;
  background: linear-gradient(90deg, #FF3CAC, #00E5FF);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

/* Benefits 2-column grid */
.sub-benefits {
  list-style: none;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px 12px;
  margin: 0;
  padding: 0;
}
.sub-benefits li {
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: 'Rajdhani', sans-serif;
  font-weight: 600;
  font-size: 12.5px;
  letter-spacing: 0.2px;
  color: rgba(255,255,255,0.82);
  line-height: 1.25;
}
.sub-check {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  border-radius: 50%;
  background: linear-gradient(135deg, #8A2BE2, #FF3CAC);
  color: #fff;
  display: grid;
  place-items: center;
  font-size: 10px;
  font-weight: 800;
  box-shadow: 0 0 0 1px rgba(255,255,255,0.12), 0 4px 10px rgba(138,43,226,0.4);
}

/* CTA button with pulsing glow */
.sub-cta {
  width: 100%;
  height: 52px;
  padding: 0 18px;
  border-radius: 999px;
  background: linear-gradient(135deg, #8A2BE2 0%, #FF3CAC 50%, #8A2BE2 100%);
  background-size: 200% auto;
  border: none;
  color: #fff;
  font-family: 'Rajdhani', sans-serif;
  font-weight: 800;
  font-size: 14px;
  letter-spacing: 1.4px;
  text-transform: uppercase;
  cursor: pointer;
  box-shadow:
    0 0 18px rgba(255,60,172,0.5),
    0 8px 26px rgba(138,43,226,0.45);
  transition: transform 0.2s, background-position 0.5s, box-shadow 0.2s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  animation: subCtaPulse 2.4s ease-in-out infinite;
  margin-top: 2px;
}
.sub-cta::after {
  content: '\\203A';
  font-family: 'Bebas Neue', sans-serif;
  font-size: 22px;
  line-height: 0.8;
  margin-left: 2px;
  transform: translateY(-1px);
}
.sub-cta:hover {
  background-position: 100% 0;
  transform: translateY(-2px);
  box-shadow:
    0 0 28px rgba(255,60,172,0.7),
    0 12px 32px rgba(138,43,226,0.6);
}
.sub-cta:active {
  transform: translateY(0);
}
.sub-cta:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  animation: none;
}

@keyframes subCtaPulse {
  0%, 100% { box-shadow: 0 0 16px rgba(255,60,172,0.4), 0 8px 24px rgba(138,43,226,0.4); }
  50%      { box-shadow: 0 0 26px rgba(255,60,172,0.7), 0 8px 28px rgba(138,43,226,0.6); }
}

/* Skip link */
.sub-skip {
  text-align: center;
  margin-top: -4px;
}
.sub-skip button {
  background: none;
  border: none;
  font-family: 'Rajdhani', sans-serif;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.4px;
  color: rgba(255,255,255,0.4);
  cursor: pointer;
  transition: color 0.2s;
  padding: 4px 8px;
}
.sub-skip button:hover {
  color: rgba(0,229,255,0.85);
}

/* Trust line */
.sub-trust {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-top: 2px;
  flex-wrap: wrap;
}
.sub-trust span {
  font-family: 'Nunito', sans-serif;
  font-size: 10.5px;
  font-weight: 600;
  color: rgba(255,255,255,0.32);
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

/* ── Responsive adjustments ── */
@media (min-width: 640px) {
  .sub-modal { max-width: 520px; }
  .sub-image { height: 420px; }
  .sub-creator-name { font-size: 44px; }
  .sub-title { font-size: 28px; }
  .sub-content { padding: 28px 30px 28px; gap: 16px; }
  .sub-cta { height: 54px; font-size: 15px; }
}

@media (max-width: 380px) {
  .sub-overlay { padding: 12px; }
  .sub-image { height: 280px; }
  .sub-creator-name { font-size: 30px; }
  .sub-content { padding: 18px; }
  .sub-benefits li { font-size: 11.5px; }
}

@media (max-height: 700px) {
  .sub-image { height: 220px; }
  .sub-creator-name { font-size: 28px; }
  .sub-content { padding: 16px 18px; gap: 10px; }
}
`

export default ModelPageSignupModal
